import { compose, groupBy, map, set, compact, isEqual, isEmpty } from 'lodash/fp';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { replace } from '@mews/routing';
import {
    createAction,
    DateTimeService,
    Interval,
    IntervalSet,
    lt,
    showErrorAlert,
    showSuccessAlert,
} from '@mews/framework';
import { TimeUnit } from '@mews/enums';
import { keyById } from '@mews/fp';
import {
    AssignmentResult,
    ResourceUseState,
    Service,
    SimpleReservationData,
    SimpleResourceUseResult,
} from '@mews-commander/data-access';
import { RouteName, FeatureFlag } from '@mews-commander/enums';
import { AsyncAction, CommanderThunkAction } from '@mews-commander/types';
import {
    createFetchAction,
    createEmptyAction,
    createBackgroundAction,
    getMissingIntervals,
    getAppDateTimeNow,
    isHourUse,
} from '@mews-commander/utils';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { fetchSpaces } from '@mews-commander/legacy-entities/src/ducks/spaces';
import { getSlotsByIds } from '@mews-commander/legacy-entities/src/ducks/resourceSlots';
import { getCurrentEnterpriseIanaTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { fetchCalendarEvents } from '@mews-commander/legacy-entities/src/ducks/calendarEvents';
import { getCurrentEnterpriseBookableServices } from '@mews-commander/legacy-entities/src/selectors/services';
import { RESOURCE_EXTENT } from '@mews-commander/legacy-entities/src/constants';
import { fetchCurrentEnterpriseReservationsByReservationIds } from '@mews-commander/legacy-entities/src/actions/reservations';
import { getResourceCategoryIdByResourceId } from '@mews-commander/legacy-entities/src/ducks/resourceCategoryAssignments';
import { getCurrentEnterpriseAppDateTimeNow } from '@mews-commander/legacy-entities/src/selectors/session/dateTime';
import { withActiveWindowLoader } from '../../../core';
import {
    getFlatTimelineResourceUses,
    getRanges,
    getLoadingRanges,
    getCustomers,
    getCurrentViewportRange,
    isPreviewDialogOpened,
    getPreviewedOptimizeRangeUTC,
    getLoadingBlocks,
    getLoadedBlocks,
} from '../reducers';
import {
    RESOURCE_USE_FETCH_LOW_PADDING,
    RESOURCE_USE_FETCH_HIGH_PADDING,
    TIMELINE_FILTER_EVENT_CATEGORY,
    TIMELINE_HEADER_EVENT_CATEGORY,
    TIMELINE_DATE_LEGEND_EVENT_CATEGORY,
} from '../constants';
import { navigateInModal, openModal } from '../../app/actions/common';
import { intervalToUTC, convertIntervalSetToUtcRanges } from '../utils/timeUnit';
import { trackEvent } from '../../../middleware/createAnalyticsMiddleware';
import { getMonthViewReservationLengthRestrictionInfoMessageDisplayed } from '../selector';
import {
    MAX_RESOURCE_FETCH_LIMIT,
    addRequestPadding,
    chunkBlocksByResources,
    chunkBlocksByTimeUnit,
    chunkIntervalsIfNeeded,
    timeUnitManipulationTimeRange,
    timeUnitRequestOffsets,
    transformReservationsToTimelineResourceUses,
    transformSimpleResourceUsesPayload,
} from '../utils/resourceUse';
import {
    areCalendarEventsFetched,
    getAssignments,
    getMissingBlocks,
    getOptimizeRangeUTC,
    getResourceInterval,
    partitionVisibleResources,
} from '../utils/assignments';
import { TimelineService } from '../timelineService';
import { getCurrentEnterpriseTimelineUrl, getSelectedTimeUnit } from '../selectors/view';
import { getReservationCanUseAvailability } from '../../reservationModule/actions';
import { AvailabilityWarningProps } from '../types';
import Model from '../components/model';
import { getTimelineUrl, getTimelineUrlParams, GetTimelineUrlParams } from '../routes';
import {
    TimelineFilterSchemaKeys,
    TimelineFilterValuesExtended,
} from '../components/TimelineFilters/timelineFilterSchema';
import { fetchGuestReservationCounts } from '../../../accounts/modules/guestInsight/queries/useGuestReservationCounts';
import { fetchGuestsTotalStaysByIds } from '../../../accounts/modules/guestInsight';
import { unsubscribeReservationUpdates, subscribeReservationUpdates } from './subscriptions';
import {
    RESERVATION_FETCH,
    TIMELINE_RESOURCE_USES_FETCH,
    RESOURCE_USES_MODIFIED,
    RESOURCE_ASSIGNED,
    UPDATE_ASSIGNED_RESOURCE_FIXED_FETCH,
    RANGE_CHANGED,
    RESET,
    CUSTOMERS_CHANGED,
    RESOURCE_USE_DESELECTED,
    RESOURCE_USE_SELECTED,
    RANGES_FETCH_STARTED,
    RANGES_FETCH_SUCCEEDED,
    RANGES_FETCH_FAILED,
    LAST_UPDATE_START_CHANGED,
    OPTIMIZE_ASSIGNMENT,
    VIEWPORT_CHANGED,
    PREVIEW_DIALOG_TOGGLED,
    CANCEL_OPTIMIZATION,
    PREVIEW_REQUEST_STARTED,
    MONTH_VIEW_RESERVATION_LENGTH_RESTRICTION_INFO_MESSAGE_DISPLAYED,
    TIMELINE_INIT,
    CHANGE_DENSITY,
    CHANGE_TIME_UNIT,
    OPEN_AVAILABILITY_WARNING_DIALOG,
    CLOSE_AVAILABILITY_WARNING_DIALOG,
    CHANGE_DENSITY_PERCENTAGE,
    BLOCKS_FETCH_STARTED,
    BLOCKS_FETCH_SUCCEEDED,
    BLOCK_CHANGED,
    UPDATE_TIMELINE_RESOURCE_USES_FETCH_SUCCEEDED,
} from './types';

export const deselectResourceUse = () => createAction(RESOURCE_USE_DESELECTED, null);

export const selectResourceUse = (resourceUse: object) => createAction(RESOURCE_USE_SELECTED, resourceUse);

export const updateSelectedResourceUse = (resourceUse: object) => createAction(RESOURCE_USE_SELECTED, resourceUse);

export function resetTimeline() {
    return createAction(RESET);
}

interface TimelineResourceUse extends SimpleReservationData {
    totalStays: number;
}

const fetchTimelineResourceUsesAction =
    ({
        enterpriseId,
        startUtc,
        endUtc,
        resourceIds,
        slotIds,
        resourceInterval,
        shouldUseRanges,
    }: $TSFixMe): CommanderThunkAction =>
    (dispatch, getState, { appClockService, repositories, featureFlagService, loggerService }) => {
        const state = getState();

        const timeUnit = getSelectedTimeUnit(state);
        const timezone = getCurrentEnterpriseIanaTimeZoneIdentifier(state);
        const missingPaddedInterval = Interval.create({
            low: TimelineService.utcToTimeUnitIndex(startUtc, timezone, timeUnit),
            high: TimelineService.utcToTimeUnitIndex(endUtc, timezone, timeUnit),
        });

        const missingPaddedIntervalArray = IntervalSet.toArray([missingPaddedInterval]);
        const loadingBlock = {
            horizontalRange: missingPaddedInterval,
            verticalRange: resourceInterval,
        };
        if (shouldUseRanges) {
            dispatch(createAction(RANGES_FETCH_STARTED, missingPaddedIntervalArray));
        } else {
            dispatch(createAction(BLOCKS_FETCH_STARTED, [loadingBlock]));
        }
        const allBookableServices = getCurrentEnterpriseBookableServices(state, {}) as Array<Service> | undefined;
        const nonHourlyBookableServices = allBookableServices?.filter((service) => !isHourUse(service));
        const services = timeUnit === TimeUnit.Month ? nonHourlyBookableServices : allBookableServices;
        const serviceIds = services?.map((service) => service.id);

        const promise = repositories.resourceUses
            .getSimpleData({
                enterpriseIds: [enterpriseId],
                serviceIds,
                timeUnitPeriod: timeUnit,
                collidingUtc: { startUtc, endUtc },
                state: ResourceUseState.Definite,
                resourceIds,
                slotIds,
            })
            .then((payload: SimpleResourceUseResult) => {
                const newState = getState();
                const currentRange = IntervalSet.fromArray(getRanges(newState));
                // @ts-expect-error $TSFixMeError: Argument of type 'any[]' is not assignable to parameter of type 'never[]'.
                const newRange = IntervalSet.addInterval(missingPaddedInterval, currentRange);
                const newRangeArray = IntervalSet.toArray(newRange);

                if (shouldUseRanges) {
                    dispatch(createAction(RANGES_FETCH_SUCCEEDED, missingPaddedIntervalArray));
                    dispatch(createAction(RANGE_CHANGED, { range: newRangeArray }));
                } else {
                    const currentLoadedBlocks = getLoadedBlocks(newState);
                    dispatch(createAction(BLOCKS_FETCH_SUCCEEDED, [loadingBlock]));
                    dispatch(createAction(BLOCK_CHANGED, { block: [...currentLoadedBlocks, loadingBlock] }));
                }

                dispatch(createAction(CUSTOMERS_CHANGED, keyById(payload.customers)));

                const advancedReturningClassificationEnabled = featureFlagService.hasFlag(
                    FeatureFlag.advancedReturningClassificationEnabled
                );

                const optimizedReturningClassificationEnabled = featureFlagService.hasFlag(
                    FeatureFlag.optimizedReturningClassificationEnabled
                );

                const guestIds = payload?.customers?.length ? payload.customers.map((customer) => customer.id) : [];

                if (advancedReturningClassificationEnabled && guestIds?.length > 0) {
                    if (optimizedReturningClassificationEnabled) {
                        void fetchGuestReservationCounts(guestIds, loggerService).then((response) => {
                            const timelineResourceUses = getFlatTimelineResourceUses(getState());
                            if (!isEmpty(response) && !isEmpty(timelineResourceUses)) {
                                const updatedTimelineResource = Object.entries(timelineResourceUses).reduce<
                                    Record<string, TimelineResourceUse>
                                >((acc, [key, item]) => {
                                    const newTotalStays: number | undefined = response[item.accountId] ?? undefined;
                                    acc[key] = {
                                        ...item,
                                        totalStays: newTotalStays ?? item.totalStays,
                                    };
                                    return acc;
                                }, {});
                                void dispatch(
                                    createAction(UPDATE_TIMELINE_RESOURCE_USES_FETCH_SUCCEEDED, updatedTimelineResource)
                                );
                            }
                        });
                    } else {
                        void fetchGuestsTotalStaysByIds(guestIds, loggerService).then((response) => {
                            const timelineResourceUses = getFlatTimelineResourceUses(getState());
                            if (response && !isEmpty(response) && !isEmpty(timelineResourceUses)) {
                                const updatedTimelineResource = Object.entries(timelineResourceUses).reduce(
                                    (acc, [key, item]) => {
                                        const newTotalStays = response[item.accountId]?.analytics.totalStays;
                                        acc[key] = {
                                            ...item,
                                            totalStays: newTotalStays ?? item.totalStays,
                                        };
                                        return acc;
                                    },
                                    {} as Record<string, TimelineResourceUse>
                                );
                                void dispatch(
                                    createAction(UPDATE_TIMELINE_RESOURCE_USES_FETCH_SUCCEEDED, updatedTimelineResource)
                                );
                            }
                        });
                    }
                }

                return transformSimpleResourceUsesPayload(payload, getState, appClockService, featureFlagService);
            })
            .catch((e: $TSFixMe) => {
                dispatch(createAction(RANGES_FETCH_FAILED, missingPaddedIntervalArray));
                throw e;
            });
        void dispatch(createBackgroundAction(TIMELINE_RESOURCE_USES_FETCH, { promise }));
    };

export function fetchTimelineResourceUses(
    startX: number,
    endX: number,
    startY: number,
    endY: number,
    model: ReturnType<typeof Model>,
    forceRefresh = false
): CommanderThunkAction {
    return (dispatch, getState, { appClockService, featureFlagService }) => {
        const withVerticalLazyLoading = featureFlagService.hasFlag(FeatureFlag.verticalLazyLoadingTimelineEnabled);

        if ((!withVerticalLazyLoading && (!startX || !endX)) || (withVerticalLazyLoading && !model)) {
            return false;
        }

        const state = getState();
        const {
            startX: viewportStartX,
            endX: viewportEndX,
            startY: viewportStartY,
            endY: viewportEndY,
        } = getCurrentViewportRange(state);

        const visibleResources = model.getVisibleResources();

        const isVisibleViewportUnchanged =
            (withVerticalLazyLoading && visibleResources.length === 0) ||
            (startX === viewportStartX && endX === viewportEndX && startY === viewportStartY && endY === viewportEndY);

        if (!forceRefresh && isVisibleViewportUnchanged) {
            return false;
        }

        const enterpriseId = getCurrentEnterpriseId();
        const timeUnit = getSelectedTimeUnit(state);
        const loadedRanges = IntervalSet.fromArray(getRanges(state));
        const loadingRanges = IntervalSet.fromArray(getLoadingRanges(state));
        // @ts-expect-error $TSFixMeError: Argument of type 'any[]' is not assignable to parameter of type 'never[]'.
        const processedRanges = IntervalSet.addSet(loadedRanges, loadingRanges);
        const timezone = getCurrentEnterpriseIanaTimeZoneIdentifier(state);
        const now = appClockService.getAppNowUtc();
        const timelineTime = DateTimeService.fromUtc(now, timezone);
        const timelineTimeOrigin = TimelineService.getCurrentTimeUnitDateTime(timeUnit) ?? timelineTime;
        const convertedRanges = convertIntervalSetToUtcRanges(processedRanges, timeUnit, timelineTimeOrigin);
        const manipulationRange = timeUnitManipulationTimeRange(timeUnit);
        const newInterval = Interval.create({ low: startX, high: endX });
        const newIntervalUtc = addRequestPadding(
            intervalToUTC(newInterval, timeUnit, timelineTimeOrigin),
            manipulationRange(-RESOURCE_USE_FETCH_LOW_PADDING),
            manipulationRange(RESOURCE_USE_FETCH_HIGH_PADDING)
        );
        const { startPaddingFunction, endPaddingFunction } = timeUnitRequestOffsets(timeUnit);

        const dateTimeNow = DateTimeService.valueOf(getAppDateTimeNow({ appClockService, timezone }));

        const verticalLazyLoadingEnabled = withVerticalLazyLoading && timeUnit === TimeUnit.Month;

        if (verticalLazyLoadingEnabled) {
            const resourceInterval = getResourceInterval(visibleResources);
            const loadedBlocks = getLoadedBlocks(state);
            const loadingBlocks = getLoadingBlocks(state);
            const processedBlocks = [...loadedBlocks, ...loadingBlocks];
            const newBlock = {
                verticalRange: resourceInterval,
                horizontalRange: Interval.create({
                    low: TimelineService.utcToTimeUnitIndex(newIntervalUtc.startUtc, timezone, timeUnit),
                    high: TimelineService.utcToTimeUnitIndex(newIntervalUtc.endUtc, timezone, timeUnit),
                }),
            };

            const missingBlocks = getMissingBlocks(newBlock, processedBlocks);

            const chunkedMissingBlocks = chunkBlocksByTimeUnit({
                blocks: chunkBlocksByResources(missingBlocks),
                timeUnit,
                timezone,
                timelineTimeOrigin,
            });

            chunkedMissingBlocks.forEach((missingBlock) => {
                const missingInterval = intervalToUTC(missingBlock.horizontalRange, timeUnit, timelineTimeOrigin);
                const missingResources = model.getResources(
                    missingBlock.verticalRange.low,
                    missingBlock.verticalRange.high
                );
                const [resourceIds, slotIds] = partitionVisibleResources(missingResources);
                const { startUtc, endUtc } = addRequestPadding(
                    missingInterval,
                    startPaddingFunction,
                    endPaddingFunction
                );

                if (!areCalendarEventsFetched(missingBlock, processedBlocks)) {
                    dispatch(fetchCalendarEvents(startUtc, endUtc, enterpriseId));
                }
                dispatch(createAction(LAST_UPDATE_START_CHANGED, dateTimeNow));
                if (resourceIds.length || slotIds.length) {
                    const splitResourceIdsArray =
                        resourceIds.length > MAX_RESOURCE_FETCH_LIMIT
                            ? pipe(resourceIds, array.splitAt(MAX_RESOURCE_FETCH_LIMIT))
                            : [resourceIds];
                    return splitResourceIdsArray.forEach((splitResourceIds) =>
                        dispatch(
                            fetchTimelineResourceUsesAction({
                                enterpriseId,
                                startUtc,
                                endUtc,
                                resourceIds: splitResourceIds,
                                slotIds,
                                resourceInterval,
                                shouldUseRanges: false,
                            })
                        )
                    );
                }
            });
        } else {
            const intervals = forceRefresh
                ? [newIntervalUtc]
                : getMissingIntervals({
                      subjectUtcRange: newIntervalUtc,
                      loadedUtcRanges: convertedRanges,
                      timezone,
                  });

            const chunkedIntervals = chunkIntervalsIfNeeded({ intervals, timeUnit, timezone });

            chunkedIntervals.forEach((interval) => {
                const { startUtc, endUtc } = addRequestPadding(interval, startPaddingFunction, endPaddingFunction);
                dispatch(fetchCalendarEvents(startUtc, endUtc, enterpriseId));
                dispatch(createAction(LAST_UPDATE_START_CHANGED, dateTimeNow));

                return dispatch(
                    fetchTimelineResourceUsesAction({
                        enterpriseId,
                        startUtc,
                        endUtc,
                        shouldUseRanges: true,
                    })
                );
            });
        }

        dispatch(createAction(VIEWPORT_CHANGED, { startX, endX, startY, endY }));
    };
}

export function deselectResourceUseWithRedirect(): CommanderThunkAction {
    return (dispatch, getState, { resolveRoute }) => {
        const { reservationId: unused, block: blockUnused, ...rest } = getTimelineUrlParams();

        const url = getTimelineUrl(resolveRoute)(rest);
        dispatch(replace(url));
        dispatch(deselectResourceUse());
    };
}

export function selectResourceUseWithRedirect(resourceUse: $TSFixMe): CommanderThunkAction {
    return (dispatch, getState, { resolveRoute }) => {
        const state = getState();
        const isPreviewOpened = isPreviewDialogOpened(state);

        const params: GetTimelineUrlParams = {};

        if (resourceUse.isReservation) {
            params.reservationId = resourceUse.id;
            params.block = undefined;
        } else {
            params.block = resourceUse.id;
            params.reservationId = undefined;
        }

        const url = getTimelineUrl(resolveRoute)({
            ...getTimelineUrlParams(),
            ...params,
        });
        if (!isPreviewOpened) {
            dispatch(replace(url));
            dispatch(selectResourceUse(resourceUse));
        }
    };
}

export function fetchReservations(reservationIds: string[]): CommanderThunkAction<AsyncAction> {
    return async (dispatch, getState, { appClockService }) => {
        const currentEnterpriseId = getCurrentEnterpriseId();
        const promise = dispatch(
            fetchCurrentEnterpriseReservationsByReservationIds({
                reservationIds,
                extent: { origin: true, accounts: true },
            })
        )
            .then(({ value }) => {
                const { accounts, reservations } = value;
                const reservationAssignedSlotIds = compose(compact, map('assignedSlotId'))(reservations);

                if (!isEmpty(reservationAssignedSlotIds)) {
                    const existingSlotIds = compose(map('id'), getSlotsByIds)(getState(), {
                        // @ts-expect-error $TSFixMeError: Type 'unknown[]' is not assignable to type 'string[]'.
                        ids: reservationAssignedSlotIds,
                    });

                    if (!isEqual(reservationAssignedSlotIds, existingSlotIds)) {
                        dispatch(fetchSpaces(currentEnterpriseId));
                    }
                }

                dispatch(createAction(CUSTOMERS_CHANGED, keyById(accounts)));
                return transformReservationsToTimelineResourceUses(value, getState, appClockService);
            })
            .catch(() => {
                dispatch(deselectResourceUse());
                // @ts-expect-error $TSFixMeError: Argument of type '{}' is not assignable to parameter of type 'ReservationsPayload'.
                return transformReservationsToTimelineResourceUses({}, getState, appClockService);
            });

        return dispatch(createFetchAction(RESERVATION_FETCH, { promise }));
    };
}

export function fetchBlock(id: string): CommanderThunkAction<AsyncAction> {
    return async (dispatch, getState, { container, appClockService, featureFlagService }) => {
        const state = getState();
        const enterpriseId = getCurrentEnterpriseId();
        const timeUnit = getSelectedTimeUnit(state);
        if (timeUnit === TimeUnit.Month && featureFlagService.hasFlag(FeatureFlag.verticalLazyLoadingTimelineEnabled)) {
            dispatch(deselectResourceUse());
        }
        const promise = container()
            .resourceBlockComponent.getAll({ enterpriseId, resourceBlockIds: [id] })
            .then(({ resourceBlocks }: $TSFixMe) =>
                transformSimpleResourceUsesPayload({ resourceBlocks }, getState, appClockService, featureFlagService)
            )
            .catch(() => {
                dispatch(deselectResourceUse());
                return transformSimpleResourceUsesPayload({}, getState, appClockService, featureFlagService);
            });

        return dispatch(createFetchAction(RESERVATION_FETCH, { promise }));
    };
}

export function removeBlock(resourceBlock: $TSFixMe): CommanderThunkAction {
    return (dispatch, getState, { appClockService, featureFlagService }) =>
        dispatch(
            createFetchAction(
                RESOURCE_USES_MODIFIED,
                transformSimpleResourceUsesPayload(
                    { resourceBlocks: [{ ...resourceBlock, isActive: false }] },
                    getState,
                    appClockService,
                    featureFlagService
                )
            )
        );
}
interface AssignResourceProps {
    resourceUseId: string;
    resourceId: string;
    checkAvailability: boolean;
    updateBlockAvailability: boolean;
}
export function assignResource({
    resourceUseId,
    resourceId,
    checkAvailability = true,
    updateBlockAvailability = false,
}: AssignResourceProps): CommanderThunkAction<AsyncAction> {
    return async (dispatch, getState, { repositories, appClockService }) => {
        const enterpriseId = getCurrentEnterpriseId();

        if (checkAvailability) {
            const state = getState();
            const resourceUses = getFlatTimelineResourceUses(state);
            // @ts-expect-error
            const reservation = resourceUses[resourceUseId];

            const assignedCategoryId = reservation?.assignedResourceId
                ? getResourceCategoryIdByResourceId(state, {
                      resourceId: reservation.assignedResourceId,
                      serviceId: reservation.serviceId,
                  }) || reservation?.requestedCategoryId
                : null;
            const selectedCategoryId = reservation?.serviceId
                ? getResourceCategoryIdByResourceId(state, { resourceId, serviceId: reservation.serviceId })
                : null;

            const canUseAvailability = await withActiveWindowLoader(async () =>
                dispatch(getReservationCanUseAvailability(selectedCategoryId, assignedCategoryId, reservation))
            );
            if (!canUseAvailability.value) {
                return dispatch(
                    openAvailabilityDialog({
                        assignedCategoryId,
                        selectedCategoryId,
                        serviceId: reservation.serviceId,
                        reservationId: resourceUseId,
                        resourceId,
                    })
                );
            }
        }
        const promise = repositories.reservations
            .updateAssignedResource({
                reservationId: resourceUseId,
                resourceId,
                fallbackToBranch: true,
                updateBlockAvailability,
            })
            .then(({ reservations, resourceBlocks }: $TSFixMe) =>
                dispatch(fetchSpaces(enterpriseId)).then(() =>
                    transformReservationsToTimelineResourceUses(
                        { reservations, resourceBlocks },
                        getState,
                        appClockService
                    )
                )
            )
            .catch((error: Error) => {
                showErrorAlert({ description: <>{error.message}</> });
                return transformReservationsToTimelineResourceUses(
                    { reservations: [], resourceBlocks: [] },
                    getState,
                    appClockService
                );
            });

        return dispatch(createFetchAction(RESOURCE_ASSIGNED, { promise })).then((value: $TSFixMe) => {
            const state = getState();
            const resourceUses = getFlatTimelineResourceUses(state);
            // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type 'KeyByIdTyped<Identifi...
            const resourceUse = resourceUses[resourceUseId];
            const customers = getCustomers(state);
            const customer = customers[resourceUse.customerId];
            dispatch(updateSelectedResourceUse({ ...resourceUse, customer }));

            return value;
        });
    };
}

export function updateAssignedResourceLocked(
    resourceUseId: $TSFixMe,
    locked: $TSFixMe
): CommanderThunkAction<AsyncAction> {
    return async (dispatch, getState, { container }) => {
        const promise = container()
            .reservationComponent.updateLocked([resourceUseId], locked)
            .then(() => {
                const resourceUses = getFlatTimelineResourceUses(getState());
                // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type 'KeyByIdTyped<Identifi...
                const resourceUse = resourceUses[resourceUseId];
                const customer = getCustomers(getState())[resourceUse.customerId];
                resourceUse.assignedResourceLocked = locked;
                resourceUse.isMoveable = resourceUse.isReservation && !resourceUse.assignedResourceLocked;
                dispatch(updateSelectedResourceUse({ ...resourceUse, customer }));

                // @ts-expect-error $TSFixMeError: Argument of type 'KeyByIdTyped<Identifiable>' is not assignable to parameter of type 'PropertyPath'.
                return compose(groupBy('serviceId'), set(resourceUseId, resourceUse))(resourceUses);
            });

        return dispatch(createFetchAction(UPDATE_ASSIGNED_RESOURCE_FIXED_FETCH, { promise }));
    };
}

export function updateAfterHouseUseChange(data: { id: string }, deleted = false): CommanderThunkAction {
    return (dispatch) => {
        if (deleted) {
            dispatch(deselectResourceUse());
            dispatch(removeBlock(data));
        } else {
            showSuccessAlert({ description: lt('SaveSuccessMessage') });
            void dispatch(fetchBlock(data.id));
        }
    };
}

export function resourceDetail(resourceId: string): CommanderThunkAction {
    return (dispatch, getState, { resolveRoute }) => {
        const url = resolveRoute(RouteName.ResourceState, { id: resourceId });
        dispatch(openModal(url));
        return dispatch(createEmptyAction());
    };
}

export const optimizeAssignment =
    (): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { container, appClockService, featureFlagService }) => {
        const { reservationComponent } = container();
        const state = getState();
        const enterpriseId = getCurrentEnterpriseId();
        const { startUtc, endUtc } = getPreviewedOptimizeRangeUTC(state);
        const promise = reservationComponent
            .optimizeAssignment({ enterpriseId, endUtc, startUtc })
            .then((response: $TSFixMe) => {
                showSuccessAlert({ description: lt('SaveSuccessMessage') });
                dispatch(closePreviewDialog());
                dispatch(subscribeReservationUpdates());
                dispatch(resetTimeline());
                return getAssignments(response, getState, appClockService, featureFlagService);
            });

        return dispatch(createFetchAction(OPTIMIZE_ASSIGNMENT, promise));
    };

const previewRequestStarted = ({ startUtc, endUtc }: $TSFixMe) =>
    createAction(PREVIEW_REQUEST_STARTED, { startUtc, endUtc });

export const previewAssignment =
    (): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { container, appClockService, featureFlagService }) => {
        const { reservationComponent } = container();
        const enterpriseId = getCurrentEnterpriseId();
        const timelineTimeOrigin = getCurrentEnterpriseAppDateTimeNow(getState(), { appClockService });
        const { startUtc, endUtc } = getOptimizeRangeUTC(getState, timelineTimeOrigin);

        dispatch(previewRequestStarted({ startUtc, endUtc }));
        const promise = reservationComponent
            .previewAssignment({ enterpriseId, endUtc, startUtc })
            .then((response: AssignmentResult) => {
                dispatch(unsubscribeReservationUpdates());
                dispatch(openPreviewDialog());
                const resourceMovementsCount = response.reservationData?.length ?? 0;
                const assignments = getAssignments(response, getState, appClockService, featureFlagService);
                return {
                    resourceMovementsCount,
                    assignments,
                };
            });

        return dispatch(createFetchAction(OPTIMIZE_ASSIGNMENT, promise));
    };

export const timelineFilterSubmit =
    (values: TimelineFilterValuesExtended): CommanderThunkAction =>
    (dispatch, getState) => {
        const state = getState();
        const {
            [TimelineFilterSchemaKeys.ServiceId]: selectedServiceId,
            [TimelineFilterSchemaKeys.FloorNumber]: selectedFloorNumbers,
            [TimelineFilterSchemaKeys.ResourceFeatureId]: selectedResourceFeatureIds,
            [TimelineFilterSchemaKeys.ResourceCategoryId]: selectedResourceCategoryIds,
            [TimelineFilterSchemaKeys.UnassignedReservationsEnabled]: selectedUnassignedReservationsEnabled,
            dateTime: selectedDateTime,
            timeUnit: selectedTimeUnit,
        } = values;

        const url = getCurrentEnterpriseTimelineUrl(state, {
            serviceId: selectedServiceId,
            // @ts-expect-error
            period: selectedTimeUnit,
            floorNumbers: selectedFloorNumbers ?? [],
            resourceFeatureIds: selectedResourceFeatureIds ?? [],
            resourceCategoryIds: selectedResourceCategoryIds ?? [],
            dateTime: selectedDateTime,
            unassignedReservationsEnabled: selectedUnassignedReservationsEnabled ? 'true' : undefined,
        });

        if (selectedTimeUnit) {
            dispatch(changeTimeUnit(selectedTimeUnit));
        }

        dispatch(navigateInModal(url));

        if ('timeUnit' in values || 'dateTime' in values) {
            dispatch(
                trackEvent({
                    eventCategory: TIMELINE_DATE_LEGEND_EVENT_CATEGORY,
                    eventAction: 'date legend clicked',
                    eventLabel: selectedTimeUnit,
                })
            );
        } else {
            dispatch(
                trackEvent({
                    eventCategory: TIMELINE_FILTER_EVENT_CATEGORY,
                    eventAction: 'time unit set',
                    eventLabel: selectedTimeUnit,
                })
            );
        }

        dispatch(resetTimeline());
    };

export const cancelAssignment = (): CommanderThunkAction => (dispatch) => {
    dispatch(closePreviewDialog());
    dispatch(subscribeReservationUpdates());
    dispatch(resetTimeline());
    return dispatch(createAction(CANCEL_OPTIMIZATION));
};

export const openPreviewDialog = () => createAction(PREVIEW_DIALOG_TOGGLED, true);
export const closePreviewDialog = () => createAction(PREVIEW_DIALOG_TOGGLED, false);

export const changeDensity =
    (density: number | string): CommanderThunkAction =>
    (dispatch, getState, { featureFlagService }) => {
        dispatch(
            trackEvent({
                eventCategory: TIMELINE_HEADER_EVENT_CATEGORY,
                eventAction: 'density changed',
                eventLabel: density,
            })
        );
        if (featureFlagService.hasFlag(FeatureFlag.percentageDensityValuesEnabled)) {
            dispatch(createAction(CHANGE_DENSITY_PERCENTAGE, density));
        } else {
            dispatch(createAction(CHANGE_DENSITY, density));
        }
    };

export const changeTimeUnit =
    (timeUnit: TimeUnit): CommanderThunkAction =>
    (dispatch, getState, { featureFlagService }) => {
        const state = getState();
        const isMonthViewReservationLengthRestrictionInfoMessageDisplayed =
            getMonthViewReservationLengthRestrictionInfoMessageDisplayed(state);
        const withVerticalLazyLoading = featureFlagService.hasFlag(FeatureFlag.verticalLazyLoadingTimelineEnabled);

        dispatch(createAction(CHANGE_TIME_UNIT, timeUnit));

        if (!isMonthViewReservationLengthRestrictionInfoMessageDisplayed && timeUnit === TimeUnit.Month) {
            dispatch(createAction(MONTH_VIEW_RESERVATION_LENGTH_RESTRICTION_INFO_MESSAGE_DISPLAYED, true));
            showSuccessAlert({
                description: lt(
                    withVerticalLazyLoading
                        ? 'TimelineMonthViewHourlyServiceRestrictionInfoMessage'
                        : 'TimelineMonthViewReservationLengthRestrictionInfoMessage'
                ),
            });
        }

        dispatch(
            trackEvent({
                eventCategory: TIMELINE_HEADER_EVENT_CATEGORY,
                eventAction: 'time unit changed',
                eventLabel: timeUnit,
            })
        );

        dispatch(resetTimeline());
    };

export const initializeTimeline =
    (enterpriseId: string): CommanderThunkAction =>
    (dispatch) => {
        // @ts-expect-error $TSFixMeError: Argument of type '{ ResourceFeatureAssignments: boolean; ResourceCategoryAssignments: boolean; ResourceCategories: boole...
        const promise = dispatch(fetchSpaces(enterpriseId, { ...RESOURCE_EXTENT, ResourceFeatureAssignments: true }));
        void dispatch(createAction(TIMELINE_INIT, promise));
    };

export const openAvailabilityDialog = (data: AvailabilityWarningProps) =>
    createAction(OPEN_AVAILABILITY_WARNING_DIALOG, data);
export const closeAvailabilityDialog = () => createAction(CLOSE_AVAILABILITY_WARNING_DIALOG);
